import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CodeUtility, EmbeddedCode, PriceUtility } from '@jms/model';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { resolveItemFromParams } from '@workbuddy/entities/shared/items';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { itemMarginCellBridgeStyles, itemMarginCellStyleId } from './item.margin.cell.renderer.component.theme';

type ItemMarginParams = ICellRendererParams & { showPricing?: boolean };

/** One line's profit and margin, from its extensions. */
type ItemLineMargin = { profit: number; percent: number };

/**
 * Margin % cell for the office Items board (WBS-12162): the line's margin over its extensions, as the
 * legacy items table showed it. Green on a profit, red on a loss. Without the pricing right it shows
 * 0.00%, as the legacy table did, so the column never leaks the prices behind it.
 */
@Component({
  selector: 'item-margin-cell-renderer',
  template: `<span class="margin-value" [class.profit]="$profit() > 0" [class.loss]="$profit() < 0">{{ $label() }}</span>`,
  styleUrls: ['./item.margin.cell.renderer.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemMarginCellRendererComponent implements ICellRendererAngularComp {

  private readonly $margin = signal<ItemLineMargin | null>(null);

  protected readonly $profit = computed(() => this.$margin()?.profit ?? 0);
  protected readonly $label = computed(() => `${(this.$margin()?.percent ?? 0).toFixed(2)}%`);

  public constructor() {

    DesignSystem.Mount(inject(DOCUMENT), itemMarginCellStyleId, itemMarginCellBridgeStyles);
  }

  public agInit(params: ItemMarginParams): void {

    this.read(params);
  }

  public refresh(params: ItemMarginParams): boolean {

    this.read(params);
    return true;
  }

  private read(params: ItemMarginParams): void {

    const item = resolveItemFromParams(params);
    this.$margin.set(params?.showPricing && item ? itemLineMargin(item) : null);
  }
}

/**
 * Column value for the Margin % cell: the figure it shows, so AG's row-diff refreshes the cell when a
 * quantity or price edit moves it. The column's own field is virtual and never carries a value.
 */
export function itemMarginCellValue(params: { data?: unknown; colDef?: { field?: string } }): number | null {

  const item = resolveItemFromParams(params as unknown as ICellRendererParams);
  if (!item) return null;

  return itemLineMargin(item).percent;
}

// PriceUtility rounds half away from zero, as the legacy table did; Math.round would move a loss's half-cent up (-0.125 → -0.12).
function itemLineMargin(item: EmbeddedCode): ItemLineMargin {

  const extSell = CodeUtility.getExtSell(item);
  const extBuy = CodeUtility.getExtBuy(item);

  return { profit: extSell - extBuy, percent: PriceUtility.calculateMargin(extBuy, extSell) };
}
